// src/stages/stage1Plant/Stage1Plant.jsx
import "./Stage1Plant.css";
import React, { Suspense, useMemo, useState, useEffect, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import { OrbitControls } from "@react-three/drei";
import { useNavigate } from "react-router-dom";

import PlantBackground360 from "./environment/PlantBackground360.jsx";
import PlantGround from "./environment/PlantGround.jsx";
import Stage1PlantLighting from "./Stage1PlantLighting.jsx";
import FallingBranches from "./environment/FallingBranches.jsx";
import CameraShakeRig from "./environment/CameraShakeRig.jsx";
import { getPlantLightingMode }  from "./environment/getPlantLightingMode.js";
import PlantAudio from "./PlantAudio.jsx";
import RainSystem from "./environment/RainSystem.jsx";
import FernSystem from "./fern/FernSystem.jsx";
import Trees from "./environment/Trees.jsx";
import Rocks from "./environment/Rocks.jsx";
import PlantLifeMeter from "./lifeMeter/PlantLifeMeter.jsx";

const MODE_CONFIG = {
  day: {
    hdr: "/stageAssets/stage1-plant/forest_cave_sky_DAY.exr",
    environmentIntensity: 1,
    backgroundIntensity: 1,
  },
  sunset: {
    hdr: "/stageAssets/stage1-plant/forest_cave_sky_SUNSET.exr",
    environmentIntensity: 0.65,
    backgroundIntensity: 0.95,
  },
  night: {
    hdr: "/stageAssets/stage1-plant/forest_cave_sky_NIGHT.exr",
    environmentIntensity: 0.2,
    backgroundIntensity: 0.195,
  },
  sunrise: {
    hdr: "/stageAssets/stage1-plant/forest_cave_sky_SUNRISE.exr",
    environmentIntensity: 0.65,
    backgroundIntensity: 0.95,
  },
  storm: {
    hdr: "/stageAssets/stage1-plant/forest_cave_sky_STORM.exr",
    environmentIntensity: 0.65,
    backgroundIntensity: 0.95,
  },
};


export default function Stage1Plant() {

  const navigate = useNavigate();

  // plant Leaves
  const [targetOpen, setTargetOpen] = useState(1);

  // plant pie chart needle angle
  const [arrowPosition, setArrowPosition] = useState(0);

  // separate weather state for storm
  const [isStorming, setIsStorming] = useState(false);
  const [activateEndTransition, setEndTransition] = useState(false);
  const [showBlackOverlay, setShowBlackOverlay] = useState(false);

  // Wind strength passed into FernSystem.
  // Calm = slight motion, storm = stronger, end transition = strongest.
  const windStrength = activateEndTransition ? 1.75 : isStorming ? 1 : 0.18;

  //camera shake on branch impact
  const controlsRef = useRef();
  const [isShakeActive, setIsShakeActive] = useState(false);
  const [shakeTrigger, setShakeTrigger] = useState(0);
  const [shakeIntensity, setShakeIntensity] = useState(0.02);

  // zen chime + delayed nav after final death blow
  const navTimeoutRef = useRef(null);

  // derive current mode from the gauge
  const baseLightingMode = useMemo(() => {
    return getPlantLightingMode(arrowPosition);
  }, [arrowPosition]);

  // force storm mode during the final end transition
  const lightingMode = activateEndTransition ? "storm" : baseLightingMode;

  const currentMode = MODE_CONFIG[lightingMode] || MODE_CONFIG.day;

useEffect(() => {
  return () => {
    if (navTimeoutRef.current) clearTimeout(navTimeoutRef.current);
  };
}, []);

// random storm trigger
useEffect(() => {
  if (activateEndTransition) return;
  if (isStorming) return;

  const interval = setInterval(() => {
    const roll = Math.random();

    if (roll < 0.28) {
      setIsStorming(true);
    }
  }, 4500);

  return () => clearInterval(interval);
}, [isStorming, activateEndTransition]);

// storm duration
useEffect(() => {
  if (!isStorming) return;
  if (activateEndTransition) return;

  const stormTimer = setTimeout(() => {
    setIsStorming(false);
  }, 4800);

  return () => clearTimeout(stormTimer);
}, [isStorming, activateEndTransition]);

  return (
    <div
      className="page in"
      style={{ width: "100vw", height: "100vh", position: "relative" }}
    >
    
      <Canvas
        shadows
        dpr={[1, 1.75]}
        camera={{ position: [0, 0.7, 0.25], fov: 85, near: 0.01, far: 200 }}
      >

        <CameraShakeRig
          shakeTrigger={shakeTrigger}
          intensity={shakeIntensity}
          duration={0.18}
          controlsRef={controlsRef}
          onShakeStart={ () => setIsShakeActive(true)}
          onShakeEnd={ () => setIsShakeActive(false)}
        />

        <Stage1PlantLighting mode={lightingMode} />

        <OrbitControls
          ref={controlsRef}
          enabled={!isShakeActive}
          enablePan={false}
          enableZoom={false}
          target={[0, 0.8, 0.02]}
          minDistance={0.0001}
          maxDistance={0.0001}
          minPolarAngle={0}
          maxPolarAngle={Math.PI}
          rotateSpeed={0.6}
        />

        <Suspense fallback={null}>
          <PlantBackground360
            hdr={currentMode.hdr}
            environmentIntensity={currentMode.environmentIntensity}
            backgroundIntensity={currentMode.backgroundIntensity}
          />
        </Suspense>

        <Suspense fallback={null}>
          <PlantGround />
        </Suspense>

        <Suspense fallback={null}>
          <FernSystem openAmount={targetOpen} windStrength={windStrength}/>
        </Suspense>

        <Suspense fallback={null}>
          <Trees />
        </Suspense>

        <Suspense fallback={null}>
          <Rocks />
        </Suspense>

        <Suspense fallback={null}>
          <RainSystem isActive={isStorming || activateEndTransition} activateEndTransition={activateEndTransition}/>
        </Suspense>

       <FallingBranches
        activateEndTransition={activateEndTransition}
        onPeripheralImpact={() => {
          setShakeIntensity(0.04);
          setShakeTrigger(prev => prev + 1);
        }}
        onFinalImpact={() => {
          setShakeIntensity(0.06); // BIG hit
          setShakeTrigger(prev => prev + 1);

          setTimeout(() => {
            setShowBlackOverlay(true);

            // completing the plant stage sets the next Stage0 cycle to human
            localStorage.setItem("stage0PlayerMode", "human");

            const chime = new Audio("/audio/Zen-Bell-Single-Chime.mp3");
            chime.volume = 0.2;

            chime.play().catch((err) => {
              console.warn("Zen chime playback blocked:", err);
            });

            const startVolume = chime.volume;
            const fadeDuration = 5000;
            const stepMs = 100;
            const steps = Math.max(1, Math.floor(fadeDuration / stepMs));
            const volumeStep = startVolume / steps;

            let currentStep = 0;

            const fadeInterval = setInterval(() => {
              currentStep += 1;
              const nextVolume = Math.max(0, startVolume - volumeStep * currentStep);
              chime.volume = nextVolume;

              if (nextVolume <= 0 || currentStep >= steps) {
                clearInterval(fadeInterval);
                chime.pause();
                chime.currentTime = 0;
              }
            }, stepMs);

            navTimeoutRef.current = setTimeout(() => {
              navigate("/Stage0");
            }, 2000);
          }, 350);
        }}
      />
      </Canvas>

      <PlantAudio 
        mode={lightingMode} 
        isStorming={isStorming} 
        activateEndTransition={activateEndTransition}
        fadeOutAllAduio={showBlackOverlay}
        volume={0.4} 
      />

      <PlantLifeMeter
        isStorming={isStorming}
        lightingMode={lightingMode}
        setArrowPosition={setArrowPosition}
        setTargetOpen={setTargetOpen}
        onTriggerEndTransition={ () => setEndTransition(true)}
      />

      <div className={`death-overlay ${showBlackOverlay ? "visible" : ""}`} />

    </div>
  );
}